import React, { useState } from 'react';
import { useConfigStore } from '@stores/configStore';
import { FileText, AlertCircle } from 'lucide-react';
import type { ARInvoiceProcessingConfig, APInvoiceProcessingConfig } from '../types/config';

export const InvoiceProcessingTab: React.FC = () => {
  const { config, updateInvoiceProcessing } = useConfigStore();
  const [activeTab, setActiveTab] = useState<'ar' | 'ap'>('ar');
  const arConfig = config.invoiceProcessing.arInvoiceProcessing;
  const apConfig = config.invoiceProcessing.apInvoiceProcessing;

  const handleARUpdate = (updates: Partial<ARInvoiceProcessingConfig>) => {
    updateInvoiceProcessing({
      arInvoiceProcessing: { ...arConfig, ...updates },
      apInvoiceProcessing: apConfig,
    });
  };

  const handleAPUpdate = (updates: Partial<APInvoiceProcessingConfig>) => {
    updateInvoiceProcessing({
      arInvoiceProcessing: arConfig,
      apInvoiceProcessing: { ...apConfig, ...updates },
    });
  };

  const handleARUBLUpdate = (key: keyof ARInvoiceProcessingConfig['ublMapping'], value: any) => {
    handleARUpdate({
      ublMapping: { ...arConfig.ublMapping, [key]: value },
    });
  };

  const handleARFTAUpdate = (key: keyof ARInvoiceProcessingConfig['ftaCompliance'], value: any) => {
    handleARUpdate({
      ftaCompliance: { ...arConfig.ftaCompliance, [key]: value },
    });
  };

  const handleARResponseFieldUpdate = (key: keyof ARInvoiceProcessingConfig['responseFieldMapping'], value: string) => {
    handleARUpdate({
      responseFieldMapping: { ...arConfig.responseFieldMapping, [key]: value },
    });
  };

  const handleAPIRNStorageUpdate = (key: keyof APInvoiceProcessingConfig['irnHashStorage'], value: any) => {
    handleAPUpdate({
      irnHashStorage: { ...apConfig.irnHashStorage, [key]: value },
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-2 mb-4">
        <FileText className="w-5 h-5 text-blue-600" />
        <h3 className="text-lg font-semibold">Invoice Processing Configuration</h3>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <div className="flex gap-4">
          {(['ar', 'ap'] as const).map((tab) => (
            <button
              key={tab}
              onClick={() => setActiveTab(tab)}
              className={`px-4 py-2 font-medium border-b-2 transition-colors ${
                activeTab === tab
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-600 hover:text-gray-900'
              }`}
            >
              {tab === 'ar' ? 'AR Invoice Processing' : 'AP Invoice Processing'}
            </button>
          ))}
        </div>
      </div>

      {/* AR Invoice Processing */}
      {activeTab === 'ar' && (
        <div className="space-y-6">
          {/* Enable/Disable */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="arEnabled"
              checked={arConfig.enabled}
              onChange={(e) => handleARUpdate({ enabled: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="arEnabled" className="text-sm font-medium text-gray-700">
              Enable AR Invoice Processing
            </label>
          </div>

          {arConfig.enabled && (
            <>
              {/* Basic Settings */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-900 mb-4">Processing Settings</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Batch Size</label>
                    <input
                      type="number"
                      value={arConfig.batchSize}
                      onChange={(e) => handleARUpdate({ batchSize: parseInt(e.target.value) })}
                      min="1"
                      max="1000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Number of invoices to process in one batch</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Processing Interval (seconds)</label>
                    <input
                      type="number"
                      value={arConfig.processingInterval}
                      onChange={(e) => handleARUpdate({ processingInterval: parseInt(e.target.value) })}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Interval between processing batches</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Retry Attempts</label>
                    <input
                      type="number"
                      value={arConfig.retryAttempts}
                      onChange={(e) => handleARUpdate({ retryAttempts: parseInt(e.target.value) })}
                      min="0"
                      max="10"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Retry Delay (seconds)</label>
                    <input
                      type="number"
                      value={arConfig.retryDelay}
                      onChange={(e) => handleARUpdate({ retryDelay: parseInt(e.target.value) })}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Timeout (seconds)</label>
                    <input
                      type="number"
                      value={arConfig.timeout}
                      onChange={(e) => handleARUpdate({ timeout: parseInt(e.target.value) })}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* UBL Mapping */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-900 mb-4">UBL Mapping Configuration</h4>
                
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="ublEnabled"
                    checked={arConfig.ublMapping.enabled}
                    onChange={(e) => handleARUBLUpdate('enabled', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label htmlFor="ublEnabled" className="text-sm font-medium text-gray-700">
                    Enable UBL XML Mapping
                  </label>
                </div>

                {arConfig.ublMapping.enabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Mapping Template</label>
                    <select
                      value={arConfig.ublMapping.mappingTemplate}
                      onChange={(e) => handleARUBLUpdate('mappingTemplate', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="standard">Standard UBL 2.1</option>
                      <option value="india">India e-Invoice (EINV)</option>
                      <option value="custom">Custom Template</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Select the UBL mapping template to use</p>
                  </div>
                )}
              </div>

              {/* FTA Compliance */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-900 mb-4">FTA Compliance Settings</h4>
                
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="ftaEnabled"
                    checked={arConfig.ftaCompliance.enabled}
                    onChange={(e) => handleARFTAUpdate('enabled', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label htmlFor="ftaEnabled" className="text-sm font-medium text-gray-700">
                    Enable FTA Compliance
                  </label>
                </div>

                {arConfig.ftaCompliance.enabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Compliance Level</label>
                    <select
                      value={arConfig.ftaCompliance.complianceLevel}
                      onChange={(e) => handleARFTAUpdate('complianceLevel', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="BASIC">Basic Compliance</option>
                      <option value="FULL">Full Compliance</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Select the level of FTA compliance required</p>
                  </div>
                )}
              </div>

              {/* Response Field Mapping */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-900 mb-4">Response Field Mapping</h4>
                <p className="text-sm text-gray-600 mb-4">Map AIS response fields to your system fields</p>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Status Field</label>
                    <input
                      type="text"
                      value={arConfig.responseFieldMapping.statusField}
                      onChange={(e) => handleARResponseFieldUpdate('statusField', e.target.value)}
                      placeholder="e.g., invoice_status"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">IRN Field</label>
                    <input
                      type="text"
                      value={arConfig.responseFieldMapping.irnField}
                      onChange={(e) => handleARResponseFieldUpdate('irnField', e.target.value)}
                      placeholder="e.g., irn"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Hash Field</label>
                    <input
                      type="text"
                      value={arConfig.responseFieldMapping.hashField}
                      onChange={(e) => handleARResponseFieldUpdate('hashField', e.target.value)}
                      placeholder="e.g., hash"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">QR Field</label>
                    <input
                      type="text"
                      value={arConfig.responseFieldMapping.qrField}
                      onChange={(e) => handleARResponseFieldUpdate('qrField', e.target.value)}
                      placeholder="e.g., qr_code"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div className="col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">UUID Field</label>
                    <input
                      type="text"
                      value={arConfig.responseFieldMapping.uuidField}
                      onChange={(e) => handleARResponseFieldUpdate('uuidField', e.target.value)}
                      placeholder="e.g., uuid"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {/* AP Invoice Processing */}
      {activeTab === 'ap' && (
        <div className="space-y-6">
          {/* Enable/Disable */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="apEnabled"
              checked={apConfig.enabled}
              onChange={(e) => handleAPUpdate({ enabled: e.target.checked })}
              className="w-4 h-4 text-blue-600 rounded"
            />
            <label htmlFor="apEnabled" className="text-sm font-medium text-gray-700">
              Enable AP Invoice Processing
            </label>
          </div>

          {apConfig.enabled && (
            <>
              {/* Basic Settings */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-900 mb-4">Processing Settings</h4>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Batch Size</label>
                    <input
                      type="number"
                      value={apConfig.batchSize}
                      onChange={(e) => handleAPUpdate({ batchSize: parseInt(e.target.value) })}
                      min="1"
                      max="1000"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Number of invoices to process in one batch</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Processing Interval (seconds)</label>
                    <input
                      type="number"
                      value={apConfig.processingInterval}
                      onChange={(e) => handleAPUpdate({ processingInterval: parseInt(e.target.value) })}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                    <p className="text-xs text-gray-500 mt-1">Interval between processing batches</p>
                  </div>
                </div>

                <div className="grid grid-cols-3 gap-4 mt-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Retry Attempts</label>
                    <input
                      type="number"
                      value={apConfig.retryAttempts}
                      onChange={(e) => handleAPUpdate({ retryAttempts: parseInt(e.target.value) })}
                      min="0"
                      max="10"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Retry Delay (seconds)</label>
                    <input
                      type="number"
                      value={apConfig.retryDelay}
                      onChange={(e) => handleAPUpdate({ retryDelay: parseInt(e.target.value) })}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Timeout (seconds)</label>
                    <input
                      type="number"
                      value={apConfig.timeout}
                      onChange={(e) => handleAPUpdate({ timeout: parseInt(e.target.value) })}
                      min="1"
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>
              </div>

              {/* AP-Specific Settings */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-900 mb-4">AP Invoice Settings</h4>
                
                <div className="space-y-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="autoCreateAP"
                      checked={apConfig.autoCreateAPInvoice}
                      onChange={(e) => handleAPUpdate({ autoCreateAPInvoice: e.target.checked })}
                      className="w-4 h-4 text-blue-600 rounded"
                    />
                    <label htmlFor="autoCreateAP" className="text-sm font-medium text-gray-700">
                      Auto-create AP Invoice in Oracle
                    </label>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Approval Workflow</label>
                    <select
                      value={apConfig.approvalWorkflow}
                      onChange={(e) => handleAPUpdate({ approvalWorkflow: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="standard">Standard Approval</option>
                      <option value="three-way">Three-Way Matching</option>
                      <option value="two-way">Two-Way Matching</option>
                      <option value="direct">Direct Posting</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Select the approval workflow for AP invoices</p>
                  </div>
                </div>
              </div>

              {/* IRN/Hash Storage */}
              <div className="border-t pt-4">
                <h4 className="font-semibold text-gray-900 mb-4">IRN/Hash Storage</h4>
                
                <div className="flex items-center gap-2 mb-4">
                  <input
                    type="checkbox"
                    id="irnStorageEnabled"
                    checked={apConfig.irnHashStorage.enabled}
                    onChange={(e) => handleAPIRNStorageUpdate('enabled', e.target.checked)}
                    className="w-4 h-4 text-blue-600 rounded"
                  />
                  <label htmlFor="irnStorageEnabled" className="text-sm font-medium text-gray-700">
                    Enable IRN/Hash Storage
                  </label>
                </div>

                {apConfig.irnHashStorage.enabled && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Storage Location</label>
                    <select
                      value={apConfig.irnHashStorage.storageLocation}
                      onChange={(e) => handleAPIRNStorageUpdate('storageLocation', e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    >
                      <option value="invoice_header">Invoice Header</option>
                      <option value="invoice_lines">Invoice Lines</option>
                      <option value="custom_field">Custom Field</option>
                      <option value="attachment">Attachment</option>
                    </select>
                    <p className="text-xs text-gray-500 mt-1">Where to store IRN and hash values</p>
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};
